import request from '../../utils/request';
import { Result } from '@/models/general';
import {
  WorkPointRuleView,
  WorkPointEstimateView,
  WorkPointBreakdownView,
  CompensationReportView,
  PayrollEntriesResultView,
  CreateWorkPointRuleInput,
  UpdateWorkPointRuleInput,
  WorkPointEstimateQuery,
  CompensationReportQuery,
  MatchFormatDurationsView,
  UpdateMatchFormatDurationsInput,
  MatchRole,
  MatchFormat,
  resolveEnum,
  CLAWBACK_CONSENT_HEADER,
} from '@/models/tournament';
import type { ClawbackConsentOption } from '@/models/tournament';

// The server's enum converter emits names; locally-created form rows use numbers.
// Normalize every rule response at the API boundary so saved rows remain editable.
function normalizeRule(rule: WorkPointRuleView): WorkPointRuleView {
  if (!Array.isArray(rule.roleFormatPoints) || rule.roleFormatPoints.length !== 16) {
    throw new Error('Incomplete work point role and format configuration');
  }
  const combinations = new Set<string>();
  return {
    ...rule,
    roleFormatPoints: rule.roleFormatPoints.map((row) => {
      const role = resolveEnum(MatchRole, row.role);
      const format = resolveEnum(MatchFormat, row.format);
      if (role === undefined || format === undefined
        || typeof MatchRole[role] !== 'string' || typeof MatchFormat[format] !== 'string') {
        throw new Error('Unsupported work point role or match format');
      }
      const key = `${role}-${format}`;
      if (combinations.has(key)) throw new Error('Duplicate work point role and match format');
      combinations.add(key);
      return { ...row, role: role as MatchRole, format: format as MatchFormat };
    }),
  };
}

export async function getCalculationRules() {
  const response = await request.get<Result<WorkPointRuleView[]>>('WorkPoint/GetCalculationRules');
  if (response.data.errors.length === 0 && response.data.payload) {
    response.data.payload = response.data.payload.map(normalizeRule);
  }
  return response.data;
}

export async function getActiveRule() {
  const response = await request.get<Result<WorkPointRuleView>>('WorkPoint/GetActiveRule');
  if (response.data.errors.length === 0 && response.data.payload) response.data.payload = normalizeRule(response.data.payload);
  return response.data;
}

export async function createRule(input: CreateWorkPointRuleInput) {
  const response = await request.post<Result<WorkPointRuleView>>('WorkPoint/CreateRule', input);
  if (response.data.errors.length === 0 && response.data.payload) response.data.payload = normalizeRule(response.data.payload);
  return response.data;
}

export async function updateRule(input: UpdateWorkPointRuleInput) {
  const response = await request.put<Result<WorkPointRuleView>>('WorkPoint/UpdateRule', input);
  if (response.data.errors.length === 0 && response.data.payload) response.data.payload = normalizeRule(response.data.payload);
  return response.data;
}

/** Company-wide match duration per format (HR/Admin). */
export async function getMatchDurations() {
  const response = await request.get<Result<MatchFormatDurationsView>>('WorkPoint/GetMatchDurations');
  return response.data;
}

/** Saves the company-wide match durations. Only matches created or edited afterwards pick them up. */
export async function updateMatchDurations(input: UpdateMatchFormatDurationsInput) {
  const response = await request.put<Result<MatchFormatDurationsView>>('WorkPoint/UpdateMatchDurations', input);
  return response.data;
}

export async function estimatePoints(query: WorkPointEstimateQuery) {
  const response = await request.get<Result<WorkPointEstimateView>>('WorkPoint/EstimatePoints', { params: query });
  return response.data;
}

export async function getMatchBreakdown(matchId: number) {
  const response = await request.get<Result<WorkPointBreakdownView[]>>(`WorkPoint/GetMatchBreakdown/${matchId}`);
  return response.data;
}

export async function getCompensationReport(query: CompensationReportQuery) {
  const response = await request.get<Result<CompensationReportView>>('WorkPoint/GetCompensationReport', { params: query });
  return response.data;
}

/** Server clamps `PageSize` to 1..100 (`WorkPointCalculationService.GetCompensationReportAsync`). */
const COMPENSATION_PAGE_SIZE = 100;
/** Runaway guard: 100 pages x 100 referees is far past any real tenant. */
const COMPENSATION_MAX_PAGES = 100;

/**
 * The compensation report is server-paged (default 25) while `totalWorkPoints` / `totalCurrencyAmount`
 * are computed over ALL officials. Reading a single page therefore produced a footer, a "Total Officials"
 * count and a CSV that silently covered 25 officials while the totals covered everyone. Walk every page
 * so line-level output and the server totals describe the same set.
 */
export async function getCompensationReportAllPages(query: CompensationReportQuery) {
  const first = await getCompensationReport({ ...query, page: 1, pageSize: COMPENSATION_PAGE_SIZE });
  if (first.errors.length > 0 || !first.payload) return first;

  const lines = [...first.payload.lines];
  const totalPages = Math.min(first.payload.totalPages ?? 1, COMPENSATION_MAX_PAGES);

  for (let page = 2; page <= totalPages; page++) {
    const next = await getCompensationReport({ ...query, page, pageSize: COMPENSATION_PAGE_SIZE });
    if (next.errors.length > 0 || !next.payload) return next; // surface the failure, never a half report
    if (next.payload.lines.length === 0) break;
    lines.push(...next.payload.lines);
  }

  return {
    ...first,
    payload: { ...first.payload, lines, page: 1, pageSize: lines.length },
  };
}

/**
 * CO-921/CO-922 Round 2: no UI caller today. When a re-price would take back pay already given, the
 * server answers 409 with a claw-back preview; a future button runs this through useClawbackConsent.
 */
export async function recalculateMatch(matchId: number, opts?: ClawbackConsentOption) {
  const response = await request.post<Result<WorkPointBreakdownView[]>>(
    `WorkPoint/RecalculateMatch/${matchId}`,
    undefined,
    opts?.clawbackConsent ? { headers: { [CLAWBACK_CONSENT_HEADER]: opts.clawbackConsent } } : undefined,
  );
  return response.data;
}

export async function generatePayrollEntries(input: { startDate: string; endDate: string; tournamentId?: number; refereeUserId?: string }) {
  const response = await request.post<Result<PayrollEntriesResultView>>('WorkPoint/GeneratePayrollEntries', input);
  return response.data;
}
